import {
  Component,
  OnInit,
  OnDestroy,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { Role } from 'src/app/model/admin/role';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ROLES_CLAIMS_TABLE_DEFINITION } from 'src/app/admin/definitions/data-table-definitions';
import { Claim } from 'src/app/model/admin/claim';
import { UpdateRoleInput } from 'src/app/model/admin/update-role-input';
import { isNullOrUndefined, sortByProperty } from 'src/app/utils/utils';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { PickList } from '@openng/optimus-ui/picklist';
import {
  ConfirmationService,
  FilterMetadata,
  PrimeTemplate,
} from '@openng/optimus-ui/api';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { RoleTableService } from 'src/app/services/table/role-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog';

@Component({
  selector: 'app-role-claim-assign-container',
  templateUrl: './role-claim-assign-container.component.html',
  styleUrls: ['./role-claim-assign-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Select,
    PickList,
    PrimeTemplate,
    ConfirmDialog,
    TranslatePipe,
  ],
})
export class RoleClaimAssignContainerComponent
  extends SearchContainerComponent<Role>
  implements OnInit, OnDestroy
{
  protected _adminBuilder = inject(AdminBuilderService);
  protected _adminApi = inject(AdminApiService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _roleTableService = inject(RoleTableService);
  protected _fb = inject(UntypedFormBuilder);
  protected _authApi = inject(AuthApiService);
  protected _confirmationService = inject(ConfirmationService);

  readonly audit = viewChild<AuditDialogContainerComponent>('audit');
  readonly pickList = viewChild<PickList>('pickList');

  canEdit = false;
  saving = false;
  roleClaimForm!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader!: string;

  rolesAndClaims: any[] = [];
  roles: Role[] = [];
  selectedRole?: Role;
  showSuccessMessage = true;
  hasLoaded = false;

  activeTabChangedSub!: Subscription;

  availableClaims: Claim[] = [];
  sourcePicklist: Claim[] = [];
  targetPicklist: Claim[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this._roleTableService.setLastLazyLoad(null);
    this.setTableService(this._roleTableService);
    this.roleClaimForm = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: true }, Validators.required],
      claims: [{ value: false, disabled: true }],
      version: [null],
    });

    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  initialize() {
    const screenName$ = this._translateService.get(
      'ROLE.SCREEN.ROLE_CLAIM_ASSIGN'
    );
    const dialogHeader$ = this._translateService.get('ROLE.LABEL.ROLE_DETAILS');
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this._adminApi.getAvailableClaims().subscribe((claims) => {
      this.availableClaims = claims;
    });

    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.ROLES.EDIT_ROLES,
    ]);
    if (this.canEdit) {
      this.roleClaimForm.enable();
    }

    this.columns = ROLES_CLAIMS_TABLE_DEFINITION;
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    const query = {
      role: null,
      claim: null,
    };

    if (filters.name) {
      query.role = (filters.name as FilterMetadata).value;
    }
    if (filters.claims) {
      query.claim = (filters.claims as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'role');

    this._roleTableService.setLastSortField('role');
    this._roleTableService.setLastSortDirection(1);

    this._adminApi
      .queryForRolesAndClaims(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (roleConnection) => {
          this.rolesAndClaims = roleConnection.items;
          this.totalRecords = roleConnection.totalRecords;
          this.elements = [...this.rolesAndClaims];
          this.loading = false;
          this.hasLoaded = true;
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'ADMIN.MESSAGES.ERROR.QUERY_ROLE_CLAIMS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
          this.loading = false;
        },
      });
  }

  //Double clicking on table row action:
  changeViewEvent($event: { data: Role }): void {
    this.selectedRole = $event.data;
    this.roleClaimForm.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      claims: $event.data.claims,
      version: $event.data.version,
    });

    // Ensure that the name field is not editable
    this.roleClaimForm.controls['name'].disable();

    // update the sourceClaims
    // update the targetClaims
    this.sourcePicklist = [];
    this.targetPicklist = [];
    const roleClaims =
      this.roleClaimForm.controls['claims'].value
        ?.split(',')
        .map((x: string) => x.trim()) ?? [];

    this.availableClaims.forEach((x) => {
      if (roleClaims.includes(x.name)) {
        this.targetPicklist.push(x);
      } else {
        this.sourcePicklist.push(x);
      }
    });

    this.sourcePicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.targetPicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.displayDialog = true;
  }

  clearForm(): void {
    this.roleClaimForm.reset();
    this.pickList()?.resetSourceFilter();
    this.pickList()?.resetTargetFilter();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedRole = undefined;
    const audit = this.audit();
  }

  //refresh button in top right
  refresh(): void {
    this.lazyLoad({ lazy: this._roleTableService.getLastLazyLoad()! }); //this triggers infinite loop
  }

  //Save button in dialog popup
  saveRole(): void {
    if (!this.canEdit) {
      return;
    }

    if (!isNullOrUndefined(this.roleClaimForm) && this.roleClaimForm.valid) {
      const updateRoleInput: UpdateRoleInput | null =
        this._adminBuilder.buildRoleClaimInput(
          this.roleClaimForm.getRawValue(),
          this.targetPicklist
        );
      this.saving = true;
      this._adminApi
        .updateRole(updateRoleInput!.id, updateRoleInput)
        .subscribe({
          next: (result) => {
            let updatedRole = this.updateRolesAndClaims(result);
            this.changeViewEvent({ data: updatedRole });
            this.clearForm();
            this.displayDialog = false;
            this.saving = false;
          },
          error: (error) => {
            this.saving = false;
            console.error('Error updating role.', error);
          },
        });
    }
  }

  updateRolesAndClaims(role: Role): Role {
    // find the role in the list
    let index = this.rolesAndClaims.findIndex((x) => x.id == role.id);
    this.rolesAndClaims[index].claims = role.claims
      .map((c) => c.name)
      .join(', ');
    return this.rolesAndClaims[index];
  }

  handleCopyClaimsChange($event: SelectChangeEvent) {
    this._translateService
      .get('ADMIN.MESSAGES.CONFIRMATION.COPY_CLAIMS', {
        source: $event.value.name,
        destination: this.selectedRole!.name,
      })
      .subscribe((message) => {
        this._confirmationService.confirm({
          message: message,
          key: 'copy-claims',
          accept: () => {
            this.saving = true;
            this._adminApi
              .copyClaims($event.value.id, this.selectedRole!.id)
              .subscribe(
                (result: Role) => {
                  let updatedRole = this.updateRolesAndClaims(result);
                  this.changeViewEvent({ data: updatedRole });
                  this.clearForm();
                  this.displayDialog = false;
                  this.saving = false;
                },
                (error) => {
                  this.saving = false;
                  console.error('Error copying claims.', error);
                }
              );
          },
        });
      });
  }
}
